var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { action, computed, observable, when, makeObservable } from 'mobx';
import { memoize } from '../../../../utils/mobx/decorators/memoize';
import { runtime } from '../../Runtime';
import { bound } from '../../../../utils/decorators/basic';
import { debounce, throttle } from '../../../../utils/helper/lodash_replacements';
export class BrowserWindow {
    parent;
    isTabFocused = true;
    orientation = window.orientation;
    get browserRuntime() {
        return this.parent;
    }
    constructor(parent) {
        makeObservable(this);
        this.parent = parent;
        window.addEventListener('focus', action(() => (this.isTabFocused = true)));
        window.addEventListener('blur', action(() => (this.isTabFocused = false)));
        window.addEventListener('scroll', this.handleScroll);
        window.addEventListener('orientationchange', this.handleOrientationChange);
        window.addEventListener('resize', this.handleResizeAsync);
        window.addEventListener('wheel', this.handleWheel);
        window.addEventListener('touchmove', this.handleTouchmove);
        window.addEventListener('touchstart', this.handleTouchstart);
        /* https://stackoverflow.com/questions/1649086/detect-rotation-of-android-phone-in-the-browser-with-javascript/6603537#6603537
         * polling necessary due to iOS Bug (values doen't change on orientation change
         * */
        setInterval(this.handleOrientationChange, 4000);
        this.handleWindowResizeAction();
    }
    get isInIframe() {
        if (typeof window === 'undefined')
            return false;
        if (!window.parent)
            return false;
        return window.location !== window.parent.location;
    }
    get windowWidthInPixel() {
        return this.browserRuntime.app.appMeasure.appWidthInPixel;
    }
    set windowWidthInPixel(width) {
        this.browserRuntime.app.appMeasure.appWidthInPixel = width;
    }
    get windowHeightInPixel() {
        return this.browserRuntime.app.appMeasure.appHeightInPixel;
    }
    set windowHeightInPixel(height) {
        this.browserRuntime.app.appMeasure.appHeightInPixel = height;
    }
    get handleWindowResize() {
        return debounce(throttle(this.handleWindowResizeAction, 700), 20).bind(this);
    }
    get scrollTop() {
        // Credits: "https://github.com/qeremy/so/blob/master/so.dom.js#L426"
        return window.pageYOffset || document.documentElement.scrollTop;
    }
    handleScroll(e) { }
    scrollBackUpAndShowHint() {
        if (!this.browserRuntime.app.topic)
            return;
        if ((this.browserRuntime.app.topic.vgwort_mode === 1 && this.browserRuntime.app.user.userData.totalAnswered < 5) ||
            runtime.browserRuntime.browserAddressBar.locationSnapshot.hash.includes('content'))
            return;
        this.browserRuntime.browserExtraContent.triggerBottomRightNotification();
        setTimeout(() => {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }, 200);
    }
    handleWheel(e) {
        const isUp = e.deltaY < 0;
        const isDown = !isUp;
        if (isUp && this.scrollTop < window.innerHeight) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }
        if (isDown && this.scrollTop < window.innerHeight && document.getElementsByClassName('AbfragerStageOuter').length === 0) {
            this.scrollBackUpAndShowHint();
            /* window.scrollTo({
              top: window.innerHeight,
              behavior: 'smooth'
            }); */
        }
    }
    touchstartY = 0;
    handleTouchstart(e) { }
    handleTouchmove(e) {
        if (this.scrollTop < window.innerHeight) {
            this.scrollBackUpAndShowHint();
        }
    }
    async handleResizeAsync(e) {
        await when(() => !!runtime.availablePackages.Defer);
        this.handleWindowResize(e);
    }
    // https://stackoverflow.com/questions/6942785/window-innerwidth-vs-document-documentelement-clientwidth
    get reliableViewportWidth() {
        return window.innerWidth && document.documentElement.clientWidth
            ? Math.min(window.innerWidth, document.documentElement.clientWidth)
            : window.innerWidth || document.documentElement.clientWidth || document.getElementsByTagName('body')[0].clientWidth;
    }
    get reliableViewportHeight() {
        const height = window.innerHeight && document.documentElement.clientHeight
            ? Math.min(window.innerHeight, document.documentElement.clientHeight)
            : window.innerHeight || document.documentElement.clientHeight || document.getElementsByTagName('body')[0].clientHeight;
        if (this.browserRuntime.browserAgent.isSafari && window.visualViewport) {
            return Math.min(height, window.visualViewport.height); // takes keyboard height into account
        }
        return height;
    }
    handleWindowResizeAction() {
        if (this.browserRuntime.browserAgent.isSafari && document.documentElement.style.getPropertyValue('--app-height') !== `${this.windowHeightInPixel}px`) {
            document.documentElement.style.setProperty('--app-height', `${this.windowHeightInPixel}px`);
        }
        this.windowWidthInPixel = this.reliableViewportWidth; // El. width minus scrollbar width;
        this.windowHeightInPixel = this.reliableViewportHeight;
        // setTimeout(this.browserRuntime.app.stage.stageAspects.manuallyMeasureAllAspects, 1000);
    }
    handleOrientationChange(e) {
        this.orientation = window.orientation;
        this.handleWindowResizeAction();
    }
}
__decorate([
    observable
], BrowserWindow.prototype, "isTabFocused", void 0);
__decorate([
    observable
], BrowserWindow.prototype, "orientation", void 0);
__decorate([
    memoize
], BrowserWindow.prototype, "isInIframe", null);
__decorate([
    computed
], BrowserWindow.prototype, "windowHeightInPixel", null);
__decorate([
    memoize
], BrowserWindow.prototype, "handleWindowResize", null);
__decorate([
    action.bound
], BrowserWindow.prototype, "handleScroll", null);
__decorate([
    action.bound
], BrowserWindow.prototype, "scrollBackUpAndShowHint", null);
__decorate([
    action.bound
], BrowserWindow.prototype, "handleWheel", null);
__decorate([
    action.bound
], BrowserWindow.prototype, "handleTouchstart", null);
__decorate([
    action.bound
], BrowserWindow.prototype, "handleTouchmove", null);
__decorate([
    bound
], BrowserWindow.prototype, "handleResizeAsync", null);
__decorate([
    action.bound
], BrowserWindow.prototype, "handleWindowResizeAction", null);
__decorate([
    action.bound
], BrowserWindow.prototype, "handleOrientationChange", null);
